'use client';

import { InfoCircledIcon } from '@radix-ui/react-icons';
import { type FC, type TouchEvent,useEffect, useMemo, useRef, useState } from 'react';

import FirstCourseMessageModal from '@/components/HomePage/FirstCourseMessageModal';
import PageWrapper from '@/components/PageWrapper';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent as TooltipContentTag,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { HOMEPAGECARDSDATA } from '@/constants/homepage';
import { useHome } from '@/context/HomeContext';
import { useProfileContext } from '@/context/ProfileContext';
import type { HomePageCardsDataType } from '@/types/HomePageCardTypes';

import { useUpdateAccountBalance } from '../api/Finances/api';

const LandingPage: FC = () => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const { profileNameDetails } = useProfileContext();
  const { mutateAsync: updateAccountBalance } = useUpdateAccountBalance();
  const { updateAccount } = useHome();

  const handleTouchToggle = (event: TouchEvent) => {
    event.stopPropagation();
    setIsTooltipOpen((prev) => !prev);
  };

  const closeTooltips = () => setIsTooltipOpen(false);

  useEffect(() => {
    const close = () => closeTooltips();
    window.addEventListener('scroll', close);
    window.addEventListener('touchstart', close);

    return () => {
      window.removeEventListener('scroll', close);
      window.removeEventListener('touchstart', close);
    };
  }, []);

  const hasRun = useRef(false);
  const Emplid = useMemo(() => profileNameDetails?.emplid, [profileNameDetails?.emplid]);

  useEffect(() => {
    if (!hasRun.current && Emplid) {
      hasRun.current = true;

      const payload = {
        UC_DU_TUIT_CALC_REQ: {
          EMPLID: Emplid ?? '',
          VERBOSE: 'N',
        },
      };

      updateAccountBalance(payload).finally(() => updateAccount());
    }
  }, [Emplid]);

  return (
    <>
      <PageWrapper className="flex flex-wrap gap-x-7 gap-y-5">
        {HOMEPAGECARDSDATA?.map(
          (
            {
              id,
              className,
              iconClassName,
              titleElement,
              icon,
              title,
              cardComponent,
              tooltip,
              TooltipContent,
              tabComponent,
            }: HomePageCardsDataType,
            i: number
          ) =>
            !tabComponent ? (
              <Card
                key={id || i}
                className={`sm:shadow-lg flex flex-col shadow ${className}`}
                aria-label={`${title.replaceAll(' ', '-')}-card`}
              >
                <CardHeader className="p-5 pt-4">
                  <div className="flex flex-row items-center gap-2">
                    <div className={iconClassName}>{icon || ''}</div>

                    <CardTitle className="text-xl font-normal" element={titleElement}>
                      {title}
                    </CardTitle>

                    {tooltip && (
                      <TooltipProvider>
                        <Tooltip
                          delayDuration={10}
                          open={isTooltipOpen}
                          onOpenChange={setIsTooltipOpen}
                        >
                          <TooltipTrigger asChild>
                            <button
                              aria-label="Class Schedule Information"
                              id="ClassScheduleInformation"
                              tabIndex={0}
                              data-testid="info"
                              onClick={(e) => e.preventDefault()}
                              onTouchEnd={handleTouchToggle}
                            >
                              <InfoCircledIcon className="size-5 text-spblue" />
                            </button>
                          </TooltipTrigger>

                          <TooltipContentTag
                            role="tooltip"
                            aria-describedby="ClassScheduleInformation"
                            className="max-w-[30rem] bg-neutral-100 font-sans text-xs font-normal leading-6 text-spblack"
                          >
                            {TooltipContent}
                          </TooltipContentTag>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                </CardHeader>

                {cardComponent}
              </Card>
            ) : (
              <Card
                key={i}
                className={`sm:shadow-lg flex h-auto flex-col overflow-hidden shadow ${className}`}
              >
                {cardComponent}
              </Card>
            )
        )}
      </PageWrapper>

      <FirstCourseMessageModal />
    </>
  );
};

export default LandingPage;
